import { goto } from '$app/navigation'
import { type ModalSettings, type ModalStore, type ToastStore } from '@skeletonlabs/skeleton'
import camelcaseKeys from 'camelcase-keys'
import Papa from 'papaparse'
import { SvelteDate, SvelteMap } from 'svelte/reactivity'
import { writable } from 'svelte/store'

import { DatasetMetadataType } from './dataset-metadata-type'
import { ILabelingInfo } from './labeling-info'
import {
  type IClassificationRunArgs,
  type IODRunArgs,
  loadDatasetQARuns,
} from '../runs/dataset-qa-runs'
import {
  type IRawStorageConfig,
  type IStorageConfig,
  parseConfigs,
} from '@routes/storage-configs/storage-configs-store'
import { encodeHTMLEntities } from '@utils/encode'
import { FetchError, handleFetch } from '@utils/fetch'
import { HirundoCSV } from '@utils/hirundo-csv.svelte'
import { modalRegistry } from '@utils/modal-registry.svelte'
import { prepareRestorableModal } from '@utils/modal-restore'
import { Product } from '@utils/product'

enum ModalityType {
  RADAR = 'RADAR',
  VISION = 'VISION',
  SPEECH = 'SPEECH',
  TABULAR = 'TABULAR',
  MULTIMODAL = 'MULTIMODAL',
  TIMESERIES = 'TIMESERIES',
}

enum MultimodalModalityType {
  VISION = 'VISION',
  RADAR = 'RADAR',
  TABULAR = 'TABULAR',
  TIMESERIES = 'TIMESERIES',
}

const allModalityTypes = [
  ModalityType.RADAR,
  ModalityType.VISION,
  ModalityType.SPEECH,
  ModalityType.TABULAR,
  ModalityType.TIMESERIES,
  ModalityType.MULTIMODAL,
]

const productToSelectableModalityTypes = {
  [Product.RADAR_QA]: [ModalityType.RADAR],
  [Product.VISION_QA]: [ModalityType.VISION],
  [Product.SPEECH_QA]: [ModalityType.SPEECH],
  [Product.TABULAR_QA]: [ModalityType.TABULAR],
  [Product.TIMESERIES_QA]: [ModalityType.TIMESERIES],
  [Product.MULTIMODAL_QA]: [ModalityType.MULTIMODAL],
}

const multimodalChildModalities = [
  ModalityType.VISION,
  ModalityType.RADAR,
  ModalityType.TABULAR,
  ModalityType.TIMESERIES,
]

const displayModalityTypes = {
  [ModalityType.RADAR]: 'Radar',
  [ModalityType.VISION]: 'Vision',
  [ModalityType.SPEECH]: 'Speech',
  [ModalityType.TABULAR]: 'Tabular',
  [ModalityType.TIMESERIES]: 'Time series',
  [ModalityType.MULTIMODAL]: 'Multimodal',
}

enum LabelingType {
  SingleLabelClassification = 'SingleLabelClassification',
  // MultiLabelClassification = 'MultiLabelClassification',
  ObjectDetection = 'ObjectDetection',
  SpeechToText = 'SpeechToText',
  ObjectSegmentation = 'ObjectSegmentation',
  SemanticSegmentation = 'SemanticSegmentation',
  PanopticSegmentation = 'PanopticSegmentation',
}

const objectOrSegmentLabelingTypes = [
  LabelingType.ObjectDetection,
  LabelingType.ObjectSegmentation,
  LabelingType.SemanticSegmentation,
  LabelingType.PanopticSegmentation,
]

const labelingTypesValues = [
  LabelingType.SingleLabelClassification,
  // LabelingType.MultiLabelClassification,
  LabelingType.ObjectDetection,
  LabelingType.ObjectSegmentation,
  LabelingType.SemanticSegmentation,
  LabelingType.PanopticSegmentation,
  LabelingType.SpeechToText,
] // Using this to determine the order that they are shown in visually

const tabularLikeLabelingTypes = [LabelingType.SingleLabelClassification]

const labelingTypesValuesByModalityType = {
  [ModalityType.RADAR]: [LabelingType.SingleLabelClassification, LabelingType.ObjectDetection],
  [ModalityType.VISION]: [
    LabelingType.SingleLabelClassification,
    LabelingType.ObjectDetection,
    LabelingType.ObjectSegmentation,
    LabelingType.SemanticSegmentation,
    LabelingType.PanopticSegmentation,
  ],
  [ModalityType.SPEECH]: [LabelingType.SpeechToText],
  [ModalityType.TABULAR]: tabularLikeLabelingTypes,
  [ModalityType.TIMESERIES]: tabularLikeLabelingTypes,
  [ModalityType.MULTIMODAL]: [LabelingType.SingleLabelClassification],
}

const modalityTypesWithData = [ModalityType.VISION, ModalityType.RADAR, ModalityType.SPEECH]

const displayLabelingTypes = {
  [LabelingType.SingleLabelClassification]: 'Single Label Classification',
  // [LabelingType.MultiLabelClassification]: 'Multi Label Classification',
  [LabelingType.ObjectDetection]: 'Object Detection',
  [LabelingType.SpeechToText]: 'Speech to Text',
  [LabelingType.ObjectSegmentation]: 'Object Segmentation',
  [LabelingType.SemanticSegmentation]: 'Semantic Segmentation',
  [LabelingType.PanopticSegmentation]: 'Panoptic Segmentation',
}

const datasetMetadataTypeValuesByLabelingType = {
  [LabelingType.SingleLabelClassification]: [
    DatasetMetadataType.HirundoCSV,
    DatasetMetadataType.MultimodalHirundoCSV,
  ],
  // [LabelingType.MultiLabelClassification]: [DatasetMetadataType.HirundoCSV],
  [LabelingType.ObjectDetection]: [
    DatasetMetadataType.HirundoCSV,
    DatasetMetadataType.COCO,
    DatasetMetadataType.YOLO,
  ],
  [LabelingType.SpeechToText]: [
    DatasetMetadataType.HirundoCSV,
    DatasetMetadataType.HuggingFaceAudio,
  ],
  [LabelingType.ObjectSegmentation]: [DatasetMetadataType.HirundoCSV],
  [LabelingType.SemanticSegmentation]: [DatasetMetadataType.HirundoCSV],
  [LabelingType.PanopticSegmentation]: [DatasetMetadataType.HirundoCSV],
}

const tabularLikeMetadataTypes = [DatasetMetadataType.HirundoCSV]

const allowedLabelingInfoByModalityType = {
  [ModalityType.RADAR]: [
    DatasetMetadataType.HirundoCSV,
    DatasetMetadataType.COCO,
    DatasetMetadataType.YOLO,
  ],
  [ModalityType.VISION]: [
    DatasetMetadataType.HirundoCSV,
    DatasetMetadataType.COCO,
    DatasetMetadataType.YOLO,
  ],
  [ModalityType.SPEECH]: [DatasetMetadataType.HirundoCSV, DatasetMetadataType.HuggingFaceAudio],
  [ModalityType.TABULAR]: tabularLikeMetadataTypes,
  [ModalityType.TIMESERIES]: tabularLikeMetadataTypes,
  [ModalityType.MULTIMODAL]: [DatasetMetadataType.MultimodalHirundoCSV],
}

const displayMetadataTypes = {
  [DatasetMetadataType.HirundoCSV]: 'Hirundo CSV',
  [DatasetMetadataType.MultimodalHirundoCSV]: 'Multimodal Hirundo CSV',
  [DatasetMetadataType.COCO]: 'COCO',
  [DatasetMetadataType.YOLO]: 'YOLO',
  [DatasetMetadataType.HuggingFaceAudio]: 'HuggingFace Audio',
  [DatasetMetadataType.HuggingFaceDataset]: 'HuggingFace Dataset',
}

enum STTLanguage {
  Arabic = 'ar',
  Hebrew = 'he',
  English = 'en',
}

const sttLanguages = [STTLanguage.English, STTLanguage.Arabic, STTLanguage.Hebrew] // Using this to determine the order that they are shown in visually

const displaySTTLanguages = {
  [STTLanguage.Arabic]: 'Arabic',
  [STTLanguage.Hebrew]: 'Hebrew',
  [STTLanguage.English]: 'English',
}

enum ImageAugmentationName {
  RandomHorizontalFlip = 'RandomHorizontalFlip',
  RandomVerticalFlip = 'RandomVerticalFlip',
  RandomRotation = 'RandomRotation',
  RandomPerspective = 'RandomPerspective',
  GaussianNoise = 'GaussianNoise',
  RandomGrayscale = 'RandomGrayscale',
  GaussianBlur = 'GaussianBlur',
}

const imageAugmentationValues = Object.values(ImageAugmentationName)

enum TabAugmentationName {
  AddGaussianNoise = 'AddGaussianNoise',
  MaskFeatures = 'MaskFeatures',
}

const tabAugmentationValues = Object.values(TabAugmentationName)

const displayAugmentationNames = {
  [ImageAugmentationName.RandomHorizontalFlip]: 'Random Horizontal Flip',
  [ImageAugmentationName.RandomVerticalFlip]: 'Random Vertical Flip',
  [ImageAugmentationName.RandomRotation]: 'Random Rotation',
  [ImageAugmentationName.RandomPerspective]: 'Random Perspective',
  [ImageAugmentationName.GaussianNoise]: 'Gaussian Noise',
  [ImageAugmentationName.RandomGrayscale]: 'Random Grayscale',
  [ImageAugmentationName.GaussianBlur]: 'Gaussian Blur',
  [TabAugmentationName.AddGaussianNoise]: 'Add Gaussian Noise',
  [TabAugmentationName.MaskFeatures]: 'Mask Features',
}

class COCO extends ILabelingInfo {
  public type: DatasetMetadataType.COCO = DatasetMetadataType.COCO
  public jsonUrl: string = $state('')

  toJSON() {
    return {
      type: this.type,
      jsonUrl: this.jsonUrl,
    }
  }
}

class YOLO extends ILabelingInfo {
  public type: DatasetMetadataType.YOLO = DatasetMetadataType.YOLO
  public dataYamlUrl: string | null = $state(null)
  public labelsDirUrl: string = $state('')

  toJSON() {
    return {
      type: this.type,
      dataYamlUrl: this.dataYamlUrl,
      labelsDirUrl: this.labelsDirUrl,
    }
  }
}

class HuggingFaceAudio extends ILabelingInfo {
  public type: DatasetMetadataType.HuggingFaceAudio = DatasetMetadataType.HuggingFaceAudio
  public audioColumn: string = $state('')
  public textColumn: string = $state('')
  public configName: string | null = $state(null)
  public dataDir: string | null = $state(null)
  public dataFiles: string | null = $state(null)
  public split: string | null = $state(null)

  toJSON() {
    return {
      type: this.type,
      audioColumn: this.audioColumn,
      textColumn: this.textColumn,
      configName: this.configName,
      dataDir: this.dataDir,
      dataFiles: this.dataFiles,
      split: this.split,
    }
  }
}

const isMultimodalTabularLikeModality = (modality: MultimodalModalityType) =>
  [MultimodalModalityType.TABULAR, MultimodalModalityType.TIMESERIES].includes(modality)

class MultimodalModalityCSV {
  modality: MultimodalModalityType = $state(MultimodalModalityType.VISION)
  labelingInfo: HirundoCSV = $state(new HirundoCSV())
  dataRootUrl: string | null = $state(null)
  augmentations: ImageAugmentationName[] | TabAugmentationName[] | null = $state(null)
  featureCols: string[] | null = $state(null)
  extraNonFeatureCols: string[] | null = $state(null)

  constructor(modality: MultimodalModalityType = MultimodalModalityType.VISION) {
    this.modality = modality
    if (isMultimodalTabularLikeModality(modality)) {
      this.extraNonFeatureCols = []
    }
  }

  toJSON() {
    return {
      modality: this.modality,
      labelingInfo: this.labelingInfo.toJSON(),
      dataRootUrl: this.dataRootUrl || null,
      augmentations: this.augmentations,
      featureCols: this.featureCols,
      extraNonFeatureCols: this.extraNonFeatureCols,
    }
  }
}

class MultimodalHirundoCSV extends ILabelingInfo {
  public type: DatasetMetadataType.MultimodalHirundoCSV = DatasetMetadataType.MultimodalHirundoCSV
  public modalityCsvs: MultimodalModalityCSV[] = $state([
    new MultimodalModalityCSV(MultimodalModalityType.VISION),
    new MultimodalModalityCSV(MultimodalModalityType.TABULAR),
  ])
  public alignmentCsvUrl: string | null = $state(null)

  toJSON() {
    return {
      type: this.type,
      modalityCsvs: this.modalityCsvs.map((entry) => entry.toJSON()),
      alignmentCsvUrl: this.alignmentCsvUrl || null,
    }
  }
}

const parseMultimodalModalityType = (value: unknown): MultimodalModalityType => {
  // Backward compatibility for datasets saved before the client modality was renamed to VISION.
  if (value === 'IMAGE') {
    return MultimodalModalityType.VISION
  }
  return value as MultimodalModalityType
}

const isHirundoCSV = (labelingInfo?: ILabelingInfo): labelingInfo is HirundoCSV => {
  return labelingInfo?.type == DatasetMetadataType.HirundoCSV
}

const isCOCO = (labelingInfo?: ILabelingInfo): labelingInfo is COCO => {
  return labelingInfo?.type == DatasetMetadataType.COCO
}

const isYOLO = (labelingInfo?: ILabelingInfo): labelingInfo is YOLO => {
  return labelingInfo?.type == DatasetMetadataType.YOLO
}

const isHuggingFaceAudio = (labelingInfo?: ILabelingInfo): labelingInfo is HuggingFaceAudio => {
  return labelingInfo?.type == DatasetMetadataType.HuggingFaceAudio
}

const isMultimodalHirundoCSV = (
  labelingInfo?: ILabelingInfo,
): labelingInfo is MultimodalHirundoCSV => {
  return labelingInfo?.type == DatasetMetadataType.MultimodalHirundoCSV
}

const removeWrappingQuotes = (value: string): string => {
  const trimmed = value.trim()
  if (
    trimmed.length > 1 &&
    ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) &&
    !trimmed.slice(1, -1).includes(',')
  ) {
    return trimmed.slice(1, -1)
  }
  return trimmed
}

const parseClasses = (rawClasses: unknown): string[] | null => {
  if (rawClasses == null) {
    return null
  }
  if (Array.isArray(rawClasses)) {
    return rawClasses.map((item) => removeWrappingQuotes(String(item)))
  }

  const asString = String(rawClasses).trim()
  const result = Papa.parse<string[]>(asString, {
    delimiter: ',',
    skipEmptyLines: true,
  })

  if (result.errors.length === 0 && result.data.length > 0) {
    const parsed = (result.data as string[][])
      .flat()
      .map((value) => removeWrappingQuotes(value))
      .filter((value) => value !== '')
    if (parsed.length > 0) {
      return parsed
    }
  }

  return [removeWrappingQuotes(asString)]
}

const parseMultimodalHirundoCSV = (json: Record<string, unknown>): MultimodalHirundoCSV => {
  const multimodalInfo = new MultimodalHirundoCSV()
  multimodalInfo.modalityCsvs = (
    (json.modalityCsvs as unknown as Record<string, unknown>[]) ?? []
  ).map((entry) => {
    const modalityCsv = new MultimodalModalityCSV(parseMultimodalModalityType(entry.modality))
    const childLabelingInfo = parseLabelingInfo(entry.labelingInfo as Record<string, unknown>)
    if (!isHirundoCSV(childLabelingInfo)) {
      throw new Error('Multimodal child metadata must be Hirundo CSV')
    }
    modalityCsv.labelingInfo = childLabelingInfo
    modalityCsv.dataRootUrl = (entry.dataRootUrl as string | null) ?? null
    modalityCsv.augmentations =
      (entry.augmentations as ImageAugmentationName[] | TabAugmentationName[] | null) ?? null
    modalityCsv.featureCols = parseClasses(entry.featureCols as string[] | string | null)
    modalityCsv.extraNonFeatureCols = parseClasses(
      entry.extraNonFeatureCols as string[] | string | null,
    )
    return modalityCsv
  })
  multimodalInfo.alignmentCsvUrl =
    typeof json.alignmentCsvUrl === 'string' ? json.alignmentCsvUrl : null
  return multimodalInfo
}

const parseLabelingInfo = (
  json: Record<string, unknown>,
): HirundoCSV | MultimodalHirundoCSV | COCO | YOLO | HuggingFaceAudio => {
  let labelingInfo: HirundoCSV | MultimodalHirundoCSV | COCO | YOLO | HuggingFaceAudio
  if (isHirundoCSV(json)) {
    labelingInfo = new HirundoCSV()
    labelingInfo.csvUrl = json.csvUrl
  } else if (isCOCO(json)) {
    labelingInfo = new COCO()
    labelingInfo.jsonUrl = json.jsonUrl
  } else if (isYOLO(json)) {
    labelingInfo = new YOLO()
    labelingInfo.dataYamlUrl = json.dataYamlUrl
    labelingInfo.labelsDirUrl = json.labelsDirUrl
  } else if (isHuggingFaceAudio(json)) {
    labelingInfo = new HuggingFaceAudio()
    labelingInfo.audioColumn = json.audioColumn
    labelingInfo.textColumn = json.textColumn
    labelingInfo.configName = json.configName ?? null
    labelingInfo.dataDir = json.dataDir ?? null
    labelingInfo.dataFiles = json.dataFiles ?? null
    labelingInfo.split = json.split ?? null
  } else if (isMultimodalHirundoCSV(json)) {
    labelingInfo = parseMultimodalHirundoCSV(json)
  } else {
    throw new Error('Invalid labeling info type')
  }
  return labelingInfo
}

// TODO: refactor fields after backend update
interface IDataset {
  id: number
  name: string
  modality: ModalityType
  labelingType: LabelingType
  language?: STTLanguage
  organizationId?: number
  storageConfig: IStorageConfig
  classes: string[] | null
  featureCols: string[] | null
  extraNonFeatureCols: string[] | null
  dataRootUrl: string | null
  labelingInfo: ILabelingInfo
  createdAt: SvelteDate
  updatedAt: SvelteDate
  creatorId: number
  creatorName?: string
  datasetMetadataType: DatasetMetadataType
  augmentations: ImageAugmentationName[] | TabAugmentationName[] | null
}

type IRawDataset = Omit<IDataset, 'storageConfig' | 'createdAt' | 'updatedAt'> & {
  storageConfig: IRawStorageConfig
  createdAt: string
  updatedAt: string
}

const datasetStore = writable(new SvelteMap<number, IDataset>())

datasetStore.subscribe((datasets) => {
  console.log('Datasets updated: ', datasets)
})

type IRawDatasetWithStorageConfig = Omit<IRawDataset, 'storageConfig'> & {
  storageConfig: IStorageConfig
}

const parseDatasets = (rawDatasets: IRawDataset | IRawDatasetWithStorageConfig): IDataset => {
  const {
    id,
    name,
    modality,
    labelingType,
    language,
    organizationId,
    classes,
    featureCols,
    extraNonFeatureCols,
    dataRootUrl,
    labelingInfo,
    createdAt,
    updatedAt,
    creatorId,
    creatorName,
    storageConfig,
    datasetMetadataType,
    augmentations,
  } = rawDatasets

  const getStorageConfig = (): IStorageConfig => {
    // Check if storageConfig is null/undefined
    if (!storageConfig) {
      throw new Error('Storage config is null or undefined')
    }

    if (storageConfig.createdAt instanceof Date && storageConfig.updatedAt instanceof Date) {
      return storageConfig as IStorageConfig
    }

    // Otherwise, parse the raw config
    return parseConfigs(storageConfig as IRawStorageConfig)
  }

  return {
    id,
    name,
    modality,
    labelingType,
    language,
    organizationId,
    classes: parseClasses(classes),
    featureCols: parseClasses(featureCols),
    extraNonFeatureCols: parseClasses(extraNonFeatureCols),
    dataRootUrl,
    labelingInfo,
    createdAt: new SvelteDate(SvelteDate.parse(createdAt)),
    updatedAt: new SvelteDate(SvelteDate.parse(updatedAt)),
    creatorId,
    creatorName,
    storageConfig: getStorageConfig(),
    datasetMetadataType,
    augmentations,
  }
}

const datasetAsyncState = writable({
  loading: false,
  error: '',
})

const loadDatasets = async () => {
  datasetAsyncState.set({
    loading: true,
    error: '',
  })
  try {
    const datasets: IRawDataset[] = camelcaseKeys(
      await (await handleFetch('/dataset-qa/dataset/')).json(),
      { deep: true },
    )
    const datasetMap = datasets.reduce((acc, dataset) => {
      acc.set(dataset.id, parseDatasets(dataset))
      return acc
    }, new SvelteMap<number, IDataset>())
    datasetStore.set(datasetMap)
    datasetAsyncState.set({
      loading: false,
      error: '',
    })
  } catch (err) {
    console.error('Failed to load datasets: ', err)
    datasetAsyncState.set({
      loading: false,
      error: 'Failed to load datasets',
    })
  }
}

const runQA = async (
  dataset: { labelingType: LabelingType },
  datasetId: number,
  toastStore: ToastStore,
  modalStore: ModalStore,
) => {
  const modal: ModalSettings = {
    type: 'component',
    component: modalRegistry.runDatasetQAModal,
    // Data
    title: 'Run dataset QA',
    body: 'You can customize run parameters in the advanced section below if you need or just click "Run now" to start the QA run.',
    buttonTextConfirm: 'Run now',
    value: dataset,

    response: async (r: IClassificationRunArgs | IODRunArgs | boolean) => {
      if (!r) {
        return
      }
      try {
        const response = await handleFetch(`/dataset-qa/run/${datasetId}`, {
          method: 'POST',
          body: r && r != true ? { runArgs: r } : undefined,
        })
        const json = await response.json()
        await loadDatasetQARuns()
        await goto('/data-qa/runs')
        toastStore.trigger({
          message: `Started QA run for dataset named <strong>${encodeHTMLEntities(json.name)}</strong>`,
          background: 'variant-filled-success',
        })
      } catch (err) {
        console.error(err)
        if (err instanceof FetchError && err.message) {
          toastStore.trigger({
            message: encodeHTMLEntities(
              `Failed to launch QA run for dataset with error: ${err.message}`,
            ),
            background: 'variant-filled-error',
            autohide: false,
          })
          throw err
        }
        toastStore.trigger({
          message: 'Failed to launch QA run for dataset',
          background: 'variant-filled-error',
        })
        throw err
      }
    },
  }
  modalStore.trigger(
    prepareRestorableModal(modalStore, toastStore, modal, {
      toastMessage: 'Run dataset QA closed. Reopen to restore your changes?',
    }),
  )
}

export type { IDataset }

export {
  allModalityTypes,
  allowedLabelingInfoByModalityType,
  COCO,
  datasetAsyncState,
  DatasetMetadataType,
  datasetMetadataTypeValuesByLabelingType,
  datasetStore,
  displayAugmentationNames,
  displayLabelingTypes,
  displayMetadataTypes,
  displayModalityTypes,
  displaySTTLanguages,
  HirundoCSV,
  HuggingFaceAudio,
  ILabelingInfo,
  ImageAugmentationName,
  imageAugmentationValues,
  isCOCO,
  isHirundoCSV,
  isHuggingFaceAudio,
  isMultimodalHirundoCSV,
  isMultimodalTabularLikeModality,
  isYOLO,
  LabelingType,
  labelingTypesValues,
  labelingTypesValuesByModalityType,
  loadDatasets,
  ModalityType,
  modalityTypesWithData,
  multimodalChildModalities,
  MultimodalHirundoCSV,
  MultimodalModalityCSV,
  MultimodalModalityType,
  objectOrSegmentLabelingTypes,
  parseClasses,
  parseDatasets,
  parseLabelingInfo,
  productToSelectableModalityTypes,
  runQA,
  STTLanguage,
  sttLanguages,
  TabAugmentationName,
  tabAugmentationValues,
  YOLO,
}
